import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, MapPin, Calendar, Hash } from "lucide-react";
import { useLanguage } from "@/i18n";

export const ProjectModal = ({ project, onClose }) => {
  const { t } = useLanguage();
  const projectImages = project?.images?.length ? project.images : project?.image ? [project.image] : [];

  return (
    <AnimatePresence>
      {project && (
        <motion.div
          data-testid="project-modal-overlay"
          initial={{ opacity: 0, pointerEvents: "auto" }}
          animate={{ opacity: 1, pointerEvents: "auto" }}
          exit={{ opacity: 0, pointerEvents: "none", transition: { duration: 0.15 } }}
          onClick={onClose}
          className="fixed inset-0 z-[100] grid place-items-center px-4"
          style={{ background: "rgba(40,35,25,0.35)", backdropFilter: "blur(3px)" }}
        >
          <motion.div
            data-testid="project-modal-sheet"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.92, rotate: -1, y: 20 }}
            animate={{ opacity: 1, scale: 1, rotate: 0, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ type: "spring", stiffness: 180, damping: 22 }}
            className="paper paper-corner-fold project-modal-scroll relative w-full max-w-3xl max-h-[88vh] paper-scroll"
          >
            <span className="tape" style={{ top: -10, left: 40, transform: "rotate(-5deg)" }} aria-hidden />
            <span className="tape" style={{ top: -10, right: 40, transform: "rotate(3deg)" }} aria-hidden />

            <button
              data-testid="btn-close-project-modal"
              onClick={onClose}
              aria-label="Close project"
              className="absolute top-5 right-5 h-9 w-9 grid place-items-center rounded-full text-ink-faint hover:text-accentRed hover:bg-[var(--bg-board-2)] transition"
            >
              <X size={18} />
            </button>

            <div className="px-8 pt-12 pb-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-faint">
                {t("projectSheet")} · {project.kind || t("built")}
              </p>
              <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-2 text-ink">
                {project.title}
              </h1>

              <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4 text-xs text-ink-soft font-mono uppercase tracking-[0.15em]">
                {project.year && <span className="inline-flex items-center gap-1.5"><Calendar size={12} /> {project.year}</span>}
                {project.location && <span className="inline-flex items-center gap-1.5"><MapPin size={12} /> {project.location}</span>}
                {project.category && <span className="inline-flex items-center gap-1.5"><Hash size={12} /> {project.category}</span>}
              </div>

              {projectImages.length > 0 && (
                <div className="mt-6 space-y-3">
                  <div className="relative overflow-hidden rounded-sm border border-[var(--paper-edge)]">
                    <img src={projectImages[0]} alt={project.title} className="w-full h-72 md:h-96 object-cover" />
                    <span className="font-hand absolute bottom-3 left-4 text-accentRed text-xl rotate-[-3deg]">
                      {project.annotation || t("mainView")}
                    </span>
                  </div>

                  {projectImages.length > 1 && (
                    <div className="grid gap-3 md:grid-cols-2">
                      {projectImages.slice(1).map((image, i) => (
                        <div key={image} className="overflow-hidden rounded-sm border border-[var(--paper-edge)]">
                          <img
                            src={image}
                            alt={`${project.title} ${i + 2}`}
                            className="h-52 w-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="mt-8 grid md:grid-cols-3 gap-6">
                <div className="md:col-span-2">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-faint mb-2">{t("description")}</p>
                  <p className="text-ink leading-relaxed">
                    {project.description || "Add a longer description for this project. Talk about the brief, the constraints, the materials, the spatial intent. Use this paper sheet to lay out drawings, plans, and ideas."}
                  </p>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-faint mb-2">{t("details")}</p>
                  <ul className="space-y-2 text-sm">
                    {(project.details || ["Type — —", "Area — —", "Status — —", "Team — —"]).map((d, i) => (
                      <li key={i} className="border-b border-dashed border-[var(--paper-edge)] pb-2 text-ink-soft">
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-dashed border-[var(--paper-edge)] flex items-center justify-between">
                <span className="font-hand text-2xl text-accentBlue rotate-[-2deg]">{t("endSheet")}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-faint">
                  {t("no")} {String(project.no || "001").padStart(3, "0")}
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProjectModal;
